import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Modal para importar de golpe un Excel con una pestaña por laboratorio.
 * El nombre de la pestaña se usa como laboratorio (KERN, SANDOZ, TEVA…).
 * Muestra los labs detectados y permite aplicar todos con un solo clic
 * o desmarcar los que no se quieran aplicar aún.
 */
export default function MultiLabImportModal({ onClose, onApplied }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [jobs, setJobs] = useState(null); // [{lab, sheet_name, job_id, blocks_count, items_count, entries_count}]
  const [selected, setSelected] = useState({}); // job_id -> true
  const [replaceAll, setReplaceAll] = useState(true);
  const [applying, setApplying] = useState(false);
  const [summary, setSummary] = useState(null); // {labs_applied, summary:[]}

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx") && !f.name.toLowerCase().endsWith(".xls")) {
      toast.error("Formato no soportado. Sube un archivo .xlsx");
      return;
    }
    setFile(f);
    setJobs(null);
    setSummary(null);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/supplier-agreements/multi-vademecum-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJobs(data.jobs || []);
      const sel = {};
      for (const j of (data.jobs || [])) sel[j.job_id] = true;
      setSelected(sel);
      toast.success(`${data.sheets_imported} pestañas parseadas de ${data.sheets_detected} detectadas`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo el archivo");
    } finally {
      setUploading(false);
    }
  };

  const apply = async () => {
    if (!jobs) return;
    const chosen = jobs.filter((j) => selected[j.job_id]);
    if (chosen.length === 0) {
      toast.error("Selecciona al menos una tarifa");
      return;
    }
    setApplying(true);
    try {
      // Necesitamos las entries del preview de cada job para aplicar.
      const payloadJobs = [];
      for (const j of chosen) {
        // Sondeamos el status del job para obtener las entries (por GH y por CN)
        const { data } = await api.get(`/supplier-agreements/${encodeURIComponent(j.lab)}/vademecum/import-jobs/${j.job_id}`);
        const entries = (data?.result?.entries || []).map((e) => ({
          gh: e.gh, discount_pct: e.discount_pct,
        }));
        const entries_by_cn = (data?.result?.entries_by_cn || []).map((e) => ({
          cn: e.cn, discount_pct: e.discount_pct, gh: e.gh || "",
        }));
        payloadJobs.push({
          lab: j.lab, entries, entries_by_cn,
          no_discount_ghs: data?.result?.no_discount_ghs || [],
          no_discount_cns: data?.result?.no_discount_cns || [],
          replace_all: replaceAll,
        });
      }
      const { data } = await api.post("/supplier-agreements/multi-vademecum-apply", { jobs: payloadJobs });
      setSummary(data);
      toast.success(`Aplicadas ${data.labs_applied} tarifas`);
      if (onApplied) setTimeout(onApplied, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error aplicando");
    } finally {
      setApplying(false);
    }
  };

  const totalItems = jobs ? jobs.reduce((s, j) => s + (j.items_count || 0), 0) : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 md:p-8 overflow-y-auto" onClick={onClose} data-testid="multi-lab-import-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl relative" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-sky-100 flex items-center justify-center">
              <UploadCloud className="h-4 w-4 text-sky-700" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Importar tarifas multi-laboratorio</h2>
              <p className="text-xs text-slate-500">Un Excel · una pestaña por laboratorio · nombres de pestaña = KERN, SANDOZ, TEVA…</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="multi-lab-import-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          {!jobs && !summary && (
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
              <UploadCloud className="h-10 w-10 mx-auto text-slate-400 mb-3" />
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={onFileChange}
                className="block mx-auto text-sm"
                data-testid="multi-lab-file-input"
              />
              {file && (
                <div className="mt-3 text-xs text-slate-500">
                  {file.name} · {(file.size / 1024).toFixed(1)} KB
                </div>
              )}
              <button
                onClick={upload}
                disabled={!file || uploading}
                className="mt-4 inline-flex items-center gap-2 bg-slate-950 text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-slate-800 disabled:opacity-40"
                data-testid="multi-lab-upload"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                Analizar pestañas
              </button>
            </div>
          )}

          {jobs && !summary && (
            <>
              <div className="text-xs text-slate-500">
                Detectadas <strong>{jobs.length}</strong> tarifas · <strong>{totalItems}</strong> items totales. Marca las que quieras aplicar:
              </div>
              <div className="space-y-2">
                {jobs.map((j) => (
                  <label key={j.job_id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer" data-testid={`multi-lab-row-${j.lab}`}>
                    <input
                      type="checkbox"
                      checked={!!selected[j.job_id]}
                      onChange={(e) => setSelected({ ...selected, [j.job_id]: e.target.checked })}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-slate-900">{j.lab}</div>
                        {(j.entries_count === 0 && (j.cn_entries_count || 0) === 0) && j.items_count > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-semibold" title="No hemos podido mapear los CN al catálogo global.">
                            <AlertCircle className="h-3 w-3" /> Sin catálogo
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 font-mono-num">
                        Pestaña: {j.sheet_name} · {j.blocks_count} tiers · {j.items_count} productos · {j.entries_count} GH + {j.cn_entries_count || 0} CN
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600 mt-2">
                <input type="checkbox" checked={replaceAll} onChange={(e) => setReplaceAll(e.target.checked)} data-testid="multi-lab-replace-all" />
                Sustituir las condiciones anteriores de cada laboratorio (desmárcalo para añadirlas a las que ya tenía)
              </label>
              <div className="pt-3 border-t border-slate-200 flex justify-between">
                <button onClick={() => { setJobs(null); setFile(null); }} className="text-sm text-slate-500 hover:text-slate-900">Volver</button>
                <button
                  onClick={apply}
                  disabled={applying || Object.values(selected).filter(Boolean).length === 0}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-md text-sm font-semibold disabled:opacity-40"
                  data-testid="multi-lab-apply"
                >
                  {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Aplicar {Object.values(selected).filter(Boolean).length} tarifas
                </button>
              </div>
            </>
          )}

          {summary && (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-emerald-900 text-sm">Importación completada</div>
                  <div className="text-xs text-emerald-800 mt-0.5">
                    {summary.labs_applied} laboratorios actualizados. Cierra este modal para ver la lista refrescada.
                  </div>
                </div>
              </div>
              <ul className="text-xs divide-y divide-slate-100 border border-slate-200 rounded-md">
                {summary.summary.map((s, i) => (
                  <li key={i} className="flex justify-between px-3 py-2">
                    <span className="font-semibold text-slate-900">{s.lab}</span>
                    <span className="font-mono-num text-slate-600">
                      {s.applied > 0 ? `${s.applied} entries${s.replace_all ? " · reemplazado" : " · merged"}` : `saltado (${s.skipped || "sin entries"})`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
